import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  FileEdit,
  XCircle,
  Clock,
  PartyPopper,
  Megaphone,
  CheckCheck,
  Bell,
} from 'lucide-react';
import { useNotifications, useMarkAllNotificationsRead } from '@/lib/queries';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageTransition, FadeIn } from '@/components/ui/motion';

const ICONS: Record<string, any> = {
  LEAVE_APPROVED: { icon: CheckCircle2, color: 'bg-accent-light text-accent' },
  LEAVE_SUBMITTED: { icon: FileEdit, color: 'bg-primary-subtle text-primary' },
  LEAVE_REJECTED: { icon: XCircle, color: 'bg-red-50 text-error' },
  LEAVE_REMINDER: { icon: Clock, color: 'bg-yellow-50 text-warning' },
  BIRTHDAY: { icon: PartyPopper, color: 'bg-purple-50 text-purple-600' },
  ANNOUNCEMENT: { icon: Megaphone, color: 'bg-blue-50 text-info' },
  DEFAULT: { icon: Megaphone, color: 'bg-gray-100 text-gray-600' },
};

function timeAgo(date: string) {
  const d = new Date(date);
  const now = Date.now();
  const diff = Math.floor((now - d.getTime()) / 1000);
  if (diff < 60) return 'Hace unos segundos';
  if (diff < 3600) return `Hace ${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `Hace ${Math.floor(diff / 3600)} h`;
  return `Hace ${Math.floor(diff / 86400)} día(s)`;
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const { data } = useNotifications();
  const markAll = useMarkAllNotificationsRead();

  return (
    <PageTransition className="flex flex-col gap-6">
      {/* Header con acento de marca */}
      <div className="flex items-center gap-3">
        <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-subtle">
          <Bell size={22} className="text-primary" />
        </span>
        <div>
          <h1 className="text-2xl font-bold text-font-primary">Notificaciones</h1>
          <p className="mt-0.5 text-sm text-font-secondary">
            Centro de notificaciones de tu cuenta
          </p>
        </div>
      </div>

      <FadeIn delay={0.05}>
        <div className="overflow-hidden rounded-xl border border-border bg-white shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border p-4">
            <Badge variant={(data?.unreadCount ?? 0) > 0 ? 'info' : 'neutral'} dot>
              {data?.unreadCount ?? 0} sin leer
            </Badge>
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAll.mutate()}
            >
              <CheckCheck size={14} /> Marcar todas como leídas
            </Button>
          </div>

          <div className="stagger divide-y divide-border-light">
            {data?.items?.map((n: any) => {
              const cfg = ICONS[n.type] ?? ICONS.DEFAULT;
              const Icon = cfg.icon;
              return (
                <button
                  key={n.id}
                  onClick={() => n.link && navigate(n.link)}
                  className={`flex w-full items-start gap-4 p-5 text-left transition hover:bg-primary-subtle/40 ${
                    !n.readAt ? 'bg-primary-subtle/20' : ''
                  }`}
                >
                  <div
                    className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${cfg.color}`}
                  >
                    <Icon size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-font-primary">{n.title}</p>
                      {!n.readAt && (
                        <span className="h-2 w-2 rounded-full bg-primary" />
                      )}
                    </div>
                    <p className="mt-1 text-sm text-font-secondary">{n.body}</p>
                    <p className="mt-2 text-xs text-font-tertiary">
                      {timeAgo(n.createdAt)}
                    </p>
                  </div>
                </button>
              );
            })}
            {(!data?.items || data.items.length === 0) && (
              <div className="flex flex-col items-center gap-2 py-16 text-center">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-bg">
                  <Bell size={22} className="text-font-tertiary" />
                </span>
                <p className="text-sm font-medium text-font-secondary">
                  No tienes notificaciones
                </p>
                <p className="text-xs text-font-tertiary">
                  Aquí aparecerán las novedades de tu cuenta.
                </p>
              </div>
            )}
          </div>
        </div>
      </FadeIn>
    </PageTransition>
  );
}
